"use client";

import { useEffect, useMemo } from "react";

import { useAdminStore, type AdminLocale } from "@/store/admin-store";

interface AdminMessages {
  common: {
    cancel: string;
    save: string;
    edit: string;
    delete: string;
    active: string;
    inactive: string;
    role: string;
    name: string;
    email: string;
    user: string;
    phone: string;
    center: string;
    status: string;
    eligibleSince: string;
    loadingError: string;
    providerFailure: string;
    serviceUnavailable: string;
    requestTimeout: string;
    createError: string;
    updateError: string;
    deleteError: string;
    noResults: string;
    from: string;
    to: string;
    searchPlaceholder: string;
    noData: string;
  };
  shell: {
    appName: string;
    appSubtitle: string;
    users: string;
    roles: string;
    sources: string;
    workflows: string;
    whatsapp: string;
    usageLog: string;
    activeChannel: string;
    ready: string;
    notReady: string;
    signOut: string;
    fallbackUserName: string;
    collapseSidebar: string;
    expandSidebar: string;
    openNavigation: string;
    closeNavigation: string;
    navigationLabel: string;
    notifications: string;
    openNotifications: string;
    noNotifications: string;
    noNotificationsDescription: string;
    openUserMenu: string;
    account: string;
  };
  signIn: {
    brandTitle: string;
    brandSubtitle: string;
    title: string;
    subtitle: string;
    noProfiles: string;
    profile: string;
    password: string;
    passwordPlaceholder: string;
    invalidPassword: string;
    loadError: string;
    submitError: string;
    permissions: string;
    submit: string;
  };
  usersPage: {
    title: string;
    subtitle: string;
    newUser: string;
    empty: string;
    userCountLabel: string;
    tableUser: string;
    tablePhone: string;
    tableRole: string;
    tableBusinessRole: string;
    tableStatus: string;
    editUser: string;
    deleteUser: string;
    createTitle: string;
    createSubtitle: string;
    editTitle: string;
    accessLevel: string;
    accessRead: string;
    accessReadWrite: string;
    accessAdmin: string;
    deleteConfirm: string;
    deleteSuccess: string;
    whatsAppMappings: string;
    whatsAppMappingsSubtitle: string;
    addWhatsAppMapping: string;
    noWhatsAppMappings: string;
    saveWhatsAppMapping: string;
    deleteWhatsAppConfirm: string;
    morePhones: string;
    testWhatsApp: string;
    testWhatsAppSuccess: string;
  };
  whatsappDelivery: {
    summaryLabel: string;
    masterShort: string;
    reportShort: string;
    dailyShort: string;
    unknownShort: string;
    onShort: string;
    offShort: string;
    masterTitle: string;
    masterHint: string;
    reportTitle: string;
    reportHint: string;
    dailyTitle: string;
    dailyHint: string;
    enabled: string;
    disabled: string;
    masterSince: string;
    reportSince: string;
    dailySince: string;
    eligibilityHint: string;
    roleHint: string;
    testHint: string;
    testSummary: string;
    configureAction: string;
    viewAction: string;
    configurationTitle: string;
    configurationSubtitle: string;
    channelsTitle: string;
    channelsHint: string;
    stagedHint: string;
    verificationTitle: string;
    verificationHint: string;
    reloadAction: string;
    reloadError: string;
  };
  rolesPage: {
    title: string;
    subtitle: string;
    empty: string;
    filteredEmpty: string;
    clearSearch: string;
    retry: string;
    roleListLabel: string;
    roleSelectorLabel: string;
    canonicalRoleLabel: string;
    generalSection: string;
    displayNameLabel: string;
    profileLabel: string;
    profileHint: string;
    centersLabel: string;
    centersHint: string;
    permissionsLabel: string;
    availablePermissions: string;
    permissionSearchLabel: string;
    permissionSearchPlaceholder: string;
    permissionSelectedCount: string;
    permissionEmpty: string;
    sensitive: string;
    unavailablePermissions: string;
    unavailablePermissionsHint: string;
    saveRole: string;
    savingRole: string;
    saveSuccess: string;
    discardChanges: string;
    unsavedChanges: string;
    roleCount: string;
    permissionCount: string;
    centerCount: string;
    validationSummary: string;
    displayNameError: string;
    profileError: string;
    changesSummary: string;
    displayNameChanged: string;
    profileChanged: string;
    centersChanged: string;
    permissionsAdded: string;
    permissionsRemoved: string;
    permissionsHint: string;
  };
  reportsPage: {
    openSchedule: string;
  };
  sourcesPage: {
    title: string;
    subtitle: string;
    addSource: string;
    integration: string;
    integrationSubtitle: string;
    powerBI: string;
    sourceActionsTitle: string;
    sourceActionsSubtitle: string;
    importPowerBI: string;
    importImage: string;
    uploadImage: string;
    uploadTitle: string;
    uploadSubtitle: string;
    chooseImage: string;
    selectedFile: string;
    selectedFiles: string;
    noFileSelected: string;
    uploadFormats: string;
    dropImages: string;
    dropImagesHint: string;
    preview: string;
    remove: string;
    clearSelection: string;
    uploadSuccessSingle: string;
    uploadSuccessMultiple: string;
    uploadAndPodcastSuccessSingle: string;
    uploadAndPodcastSuccessMultiple: string;
    podcastBuildError: string;
    importTitle: string;
    importSubtitle: string;
    reportLabel: string;
    pageLabel: string;
    processLabel: string;
    processHint: string;
    workspaceLabel: string;
    refreshPowerBI: string;
    powerBIRefreshSuccess: string;
    powerBIReportsCount: string;
    powerBIPagesCount: string;
    powerBILastSync: string;
    dailyPodcastPolicyTitle: string;
    dailyPodcastPolicySubtitle: string;
    dailyPodcastPolicyStatusReady: string;
    dailyPodcastPolicyUnavailable: string;
    dailyPodcastPolicyModeLabel: string;
    dailyPodcastPolicyReadyMode: string;
    dailyPodcastPolicyReadyHint: string;
    dailyPodcastPolicyFixedMode: string;
    dailyPodcastPolicyFixedHint: string;
    dailyPodcastPolicyTimeLabel: string;
    dailyPodcastPolicyTimeHint: string;
    dailyPodcastPolicyTimeRequired: string;
    dailyPodcastPolicyUpdatedAt: string;
    dailyPodcastPolicyRetry: string;
    dailyPodcastPolicySave: string;
    dailyPodcastPolicySaving: string;
    dailyPodcastPolicyUpdateSuccess: string;
    dailyPolicyDeadlineLabel: string;
    dailyPolicyDeadlineHint: string;
    dailyPolicyDeadlineRequired: string;
    dailyPolicyIncompleteLabel: string;
    dailyPolicyIncompletePublishPartial: string;
    dailyPolicyIncompleteFail: string;
    dailyPolicyIncompleteWait: string;
    dailyPolicyAllowEmptyLabel: string;
    dailyPolicyAllowEmptyHint: string;
    dailyPolicyLateArrivalLabel: string;
    dailyPolicyLateArrivalNext: string;
    dailyPolicyLateArrivalRegenerate: string;
    dailyPolicyLateArrivalReview: string;
    schedulesTitle: string;
    schedulesSubtitle: string;
    newSchedule: string;
    editSchedule: string;
    noSchedules: string;
    createScheduleTitle: string;
    createScheduleSubtitle: string;
    editScheduleTitle: string;
    editScheduleSubtitle: string;
    reportTypeLabel: string;
    reportTypePlaceholder: string;
    frequencyLabel: string;
    frequencyDaily: string;
    frequencyWeekly: string;
    frequencyMonthly: string;
    weeklyWeekdaysLabel: string;
    weekdayMonday: string;
    weekdayTuesday: string;
    weekdayWednesday: string;
    weekdayThursday: string;
    weekdayFriday: string;
    weekdaySaturday: string;
    weekdaySunday: string;
    monthlyDayLabel: string;
    monthlyInvalidDayPolicyLabel: string;
    invalidDayPolicyLastDay: string;
    invalidDayPolicySkip: string;
    misfirePolicyLabel: string;
    misfirePolicyLatest: string;
    misfirePolicyCatchUp: string;
    misfirePolicySkip: string;
    misfirePolicyHint: string;
    catchUpLimitLabel: string;
    catchUpLimitHint: string;
    scheduleTimeOfDayLabel: string;
    scheduleTimeOfDayHint: string;
    scheduleTimeOfDayPlaceholder: string;
    activeScheduleLabel: string;
    activeScheduleHint: string;
    scheduleCreateSuccess: string;
    scheduleUpdateSuccess: string;
    scheduleDeleteSuccess: string;
    scheduleRunSuccess: string;
    scheduleRunAllSuccess: string;
    scheduleRunAllPartialError: string;
    scheduleValidationError: string;
    scheduleRepairRequired: string;
    runNow: string;
    runningNow: string;
    runAllNow: string;
    runningAllNow: string;
    scheduleDeleteConfirmTitle: string;
    scheduleDeleteConfirmSubtitle: string;
    scheduleFilterAll: string;
    scheduleFilterActive: string;
    scheduleFilterError: string;
    scheduleFilterScheduled: string;
    scheduleFilterPaused: string;
    scheduleSortLabel: string;
    scheduleSortNextRun: string;
    scheduleSortLastRun: string;
    scheduleSortStatus: string;
    scheduleSortReport: string;
    scheduleSortNextMaterialization: string;
    scheduleAutoRefresh: string;
    scheduleAutoRefreshHint: string;
    scheduleRefreshError: string;
    tableType: string;
    tableReport: string;
    tableReportHint: string;
    tablePage: string;
    tableSchedule: string;
    tableScheduleHint: string;
    tableRuntime: string;
    tableRuntimeHint: string;
    tableActions: string;
    retryMinutesShort: string;
    scheduleNever: string;
    scheduleLastRun: string;
    scheduleScheduledFor: string;
    scheduleNextAttempt: string;
    scheduleNextMaterialization: string;
    scheduleStatusScheduled: string;
    scheduleStatusPaused: string;
    scheduleStatusSuccess: string;
    scheduleStatusError: string;
    scheduleLastError: string;
    viewWorkflows: string;
    loadingReports: string;
    loadingPages: string;
    selectReportPlaceholder: string;
    selectPagePlaceholder: string;
    noReports: string;
    noPages: string;
    importSuccess: string;
    powerBIDetailUnavailable: string;
    empty: string;
    emptyFiltered: string;
    archiveTitle: string;
    archiveSubtitle: string;
    archiveFilterLabel: string;
    archiveSortLabel: string;
    openImage: string;
    noArchivedImages: string;
    allImages: string;
    processedOnly: string;
    pendingOnly: string;
    sortNewest: string;
    sortOldest: string;
    sourceManual: string;
    sourcePowerBI: string;
    selectImage: string;
    selectedCount: string;
    clearSelectedArchive: string;
    selectAllVisible: string;
    bulkDelete: string;
    bulkDeleteConfirm: string;
    bulkDeleteSuccess: string;
    viewGeneratedReport: string;
    generatedReportTitle: string;
    generatedReportSubtitle: string;
    loadingGeneratedReport: string;
    generatedReportEmpty: string;
    summaryTitle: string;
    podcastTitle: string;
    podcastScriptTitle: string;
    loadingPodcastAudio: string;
    podcastAudioUnavailable: string;
    podcastScriptUnavailable: string;
    podcastAvailable: string;
    insightsTitle: string;
    metricsTitle: string;
    metricName: string;
    metricValue: string;
    audienceLabel: string;
    recommendationLabel: string;
    cageLabel: string;
    priorityLabel: string;
    previousReport: string;
    nextReport: string;
    exportPdf: string;
    viewFullscreen: string;
    deleteImage: string;
    deleting: string;
    deleteConfirm: string;
    deleteSuccess: string;
    sourceLabel: string;
    statusLabel: string;
    centerLabel: string;
    processed: string;
    updated: string;
    files: string;
    assignedRoles: string;
    image: string;
    file: string;
    workflowOwned: string;
    viewWorkflow: string;
    immutableRecord: string;
  };
  workflowsPage: {
    title: string;
    subtitle: string;
    backToList: string;
    refresh: string;
    loadMore: string;
    kindLabel: string;
    statusLabel: string;
    allKinds: string;
    allStatuses: string;
    kindReport: string;
    kindDaily: string;
    statusPending: string;
    statusActive: string;
    statusWaiting: string;
    statusRetryWait: string;
    statusPublished: string;
    statusFailed: string;
    statusCancelled: string;
    stageStatusRunning: string;
    stageStatusSucceeded: string;
    stageStatusSkipped: string;
    attemptLostLease: string;
    deliveryPending: string;
    deliverySending: string;
    deliveryRetryWait: string;
    deliverySent: string;
    deliveryFailed: string;
    eventReportPublished: string;
    eventDailyPodcastPublished: string;
    noWorkflows: string;
    occurrence: string;
    reportRun: string;
    runId: string;
    occurrenceKey: string;
    currentStage: string;
    nextAction: string;
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
    trigger: string;
    configuration: string;
    stages: string;
    attempts: string;
    audits: string;
    delivery: string;
    noAttempts: string;
    noAudits: string;
    noDelivery: string;
    retry: string;
    cancel: string;
    rerunStage: string;
    retryDelivery: string;
    reasonLabel: string;
    reasonPlaceholder: string;
    reasonRequired: string;
    chooseStage: string;
    rerunWarning: string;
    confirmAction: string;
    provider: string;
    model: string;
    attemptCount: string;
    failureCount: string;
    nextAttempt: string;
    checkpoint: string;
    dependencies: string;
    outcome: string;
    actor: string;
    action: string;
    details: string;
    eventType: string;
    publication: string;
    publicRecords: string;
    safeFailure: string;
    publishedDeliveryNote: string;
    waitingNote: string;
    retryWaitNote: string;
    batchSummary: string;
    batchUnknown: string;
    stagePowerBIExportStart: string;
    stagePowerBIExportWait: string;
    stagePowerBIExportDownload: string;
    stageCollectDailyInputs: string;
    stageExtractReportVisualData: string;
    stageAssembleReportContext: string;
    stageAnalyzeReportContext: string;
    stageGenerateReportScript: string;
    stageAssembleDailyContext: string;
    stageSynthesizeDailyBrief: string;
    stageGenerateDailyScript: string;
    stageSynthesizeAudio: string;
    stageNormalizeAudio: string;
    stageRenderVideo: string;
    stagePublish: string;
    republishDaily: string;
    republishDailyHint: string;
    republishDailyWithRag: string;
    republishDailyWithRagHint: string;
    republishResultTitle: string;
    republishResultSkippedWhatsapp: string;
    republishResultQueuedWhatsapp: string;
    republishResultRagAttempted: string;
    republishResultRagAlreadyUploaded: string;
    republishResultRagDisabled: string;
    republishError: string;
    podcastProvenanceMissing: string;
    republishResultRagSkipped: string;
  };
  usagePage: {
    title: string;
    subtitle: string;
    delivered: string;
    pending: string;
    error: string;
    filters: string;
    allUsers: string;
    allStatuses: string;
    clearFilters: string;
    deliveries: string;
    empty: string;
    tableUser: string;
    tableDateTime: string;
    tableReport: string;
    tableStatus: string;
    tableRecipient: string;
    tableChannel: string;
    tableTrigger: string;
    tableActions: string;
    messageWithMedia: string;
    message: string;
    testTag: string;
    reportId: string;
    noReportId: string;
    unknownChannel: string;
    unknownTrigger: string;
    withMediaCount: string;
    deleteConfirm: string;
    deleteSuccess: string;
    deleting: string;
    deleteAll: string;
    deleteAllConfirm: string;
    deleteAllSuccess: string;
    deletingAll: string;
    resetResend: string;
    resetResendConfirm: string;
    resetResendSuccess: string;
    resettingResend: string;
    resetResendAll: string;
    resetResendAllConfirm: string;
    resetResendAllSuccess: string;
    resettingResendAll: string;
  };
  whatsappPage: {
    title: string;
    subtitle: string;
    addRecipient: string;
    weeklyTitle: string;
    weeklySubtitle: string;
    viewFullLog: string;
    noDeliveries: string;
    deliveriesLoadingError: string;
    deliverySingular: string;
    deliveryPlural: string;
    deliveryListLabel: string;
    recentDeliveriesNotice: string;
    contentReport: string;
    contentDailyPodcast: string;
    noContentReference: string;
    sourceAutomation: string;
    sourceReportReady: string;
    sourceDailyReady: string;
    sourceManual: string;
    sourceManualResend: string;
    sourceBulkResend: string;
    sourceUnknown: string;
    statusProcessing: string;
    statusSent: string;
    statusDelivered: string;
    statusRead: string;
    statusError: string;
    recipientsTitle: string;
    recipientsSubtitle: string;
    emptyRecipients: string;
    noSearchResults: string;
    recipientSingular: string;
    recipientPlural: string;
    tableUser: string;
    tablePhone: string;
    tableRole: string;
    tableActivation: string;
    tableStatus: string;
    tableActions: string;
    inheritRole: string;
    roleCeo: string;
    roleProductionManager: string;
    roleCenterManager: string;
    roleVeterinarian: string;
    roleQualityManager: string;
    roleOperations: string;
    roleFinance: string;
    createTitle: string;
    createSubtitle: string;
    editTitle: string;
    editSubtitle: string;
    fieldUser: string;
    fieldPhone: string;
    fieldRole: string;
    activeTitle: string;
    activeSubtitle: string;
    activeStatus: string;
    inactiveStatus: string;
    enableRecipient: string;
    disableRecipient: string;
    editAction: string;
    deleteAction: string;
    createAction: string;
    saveChanges: string;
    deleteConfirm: string;
    monday: string;
    tuesday: string;
    wednesday: string;
    thursday: string;
    friday: string;
    saturday: string;
    sunday: string;
  };
}

const messages: Record<AdminLocale, AdminMessages> = {
  es: {
    common: {
      cancel: "Cancelar",
      save: "Guardar",
      edit: "Editar",
      delete: "Borrar",
      active: "Activo",
      inactive: "Inactivo",
      role: "Rol",
      name: "Nombre",
      email: "Correo",
      user: "Usuario",
      phone: "Telefono",
      center: "Centro",
      status: "Estado",
      eligibleSince: "Elegible desde",
      loadingError: "No se pudo cargar.",
      providerFailure: "El proveedor no pudo completar la solicitud.",
      serviceUnavailable: "El servicio no está disponible o no está configurado.",
      requestTimeout: "La solicitud al proveedor agotó el tiempo de espera.",
      createError: "No se pudo crear.",
      updateError: "No se pudo actualizar.",
      deleteError: "No se pudo borrar.",
      noResults: "Sin coincidencias.",
      from: "Desde",
      to: "Hasta",
      searchPlaceholder: "Buscar por nombre o contenido...",
      noData: "Sin datos.",
    },
    shell: {
      appName: "miVocero",
      appSubtitle: "Reportes por voz",
      users: "Usuarios",
      roles: "Roles",
      sources: "Fuentes",
      workflows: "Flujos",
      whatsapp: "WhatsApp",
      usageLog: "Registro de uso",
      activeChannel: "Canal activo",
      ready: "Listo",
      notReady: "No listo",
      signOut: "Salir",
      fallbackUserName: "Admin",
      collapseSidebar: "Contraer barra lateral",
      expandSidebar: "Expandir barra lateral",
      openNavigation: "Abrir navegación",
      closeNavigation: "Cerrar navegación",
      navigationLabel: "Navegación principal",
      notifications: "Notificaciones",
      openNotifications: "Abrir notificaciones",
      noNotifications: "No hay notificaciones",
      noNotificationsDescription: "Las novedades de tu cuenta aparecerán aquí.",
      openUserMenu: "Abrir menú de usuario",
      account: "Cuenta",
    },
    signIn: {
      brandTitle: "Vocero Admin",
      brandSubtitle: "Acceso administrativo",
      title: "Ingresar",
      subtitle: "Selecciona un perfil demo del backend.",
      noProfiles: "Sin perfiles demo.",
      profile: "Perfil",
      password: "Password",
      passwordPlaceholder: "Password",
      invalidPassword: "Password no valida.",
      loadError: "No se pudo cargar.",
      submitError: "No se pudo iniciar sesion.",
      permissions: "Permisos",
      submit: "Entrar",
    },
    usersPage: {
      title: "Usuarios",
      subtitle: "Gestiona quien recibe los reportes estrategicos.",
      newUser: "Nuevo usuario",
      empty: "Sin usuarios.",
      userCountLabel: "Usuario",
      tableUser: "USUARIO",
      tablePhone: "CELULAR",
      tableRole: "CARGO",
      tableBusinessRole: "CARGO",
      tableStatus: "ESTADO",
      editUser: "Editar usuario",
      deleteUser: "Eliminar usuario",
      createTitle: "Nuevo usuario",
      createSubtitle: "Crea un perfil y asigna un rol.",
      editTitle: "Editar usuario",
      accessLevel: "Acceso",
      accessRead: "Solo lectura",
      accessReadWrite: "Lectura + escritura",
      accessAdmin: "Admin",
      deleteConfirm: "Eliminar este usuario?",
      deleteSuccess: "Usuario eliminado correctamente.",
      whatsAppMappings: "Mapeos de WhatsApp",
      whatsAppMappingsSubtitle:
        "Asocia números y configura por separado el mapeo maestro, los podcasts de reportes y el podcast diario.",
      addWhatsAppMapping: "Agregar numero",
      noWhatsAppMappings: "Este usuario no tiene numeros asociados.",
      saveWhatsAppMapping: "Guardar numero",
      deleteWhatsAppConfirm: "Eliminar este mapeo de WhatsApp?",
      morePhones: "mas",
      testWhatsApp: "Probar conexión",
      testWhatsAppSuccess: "Prueba de conexión solicitada. Revisa el estado de entrega.",
    },
    whatsappDelivery: {
      summaryLabel: "Estado de entrega",
      masterShort: "Maestro",
      reportShort: "Reportes",
      dailyShort: "Diario",
      unknownShort: "Sin confirmar",
      onShort: "Sí",
      offShort: "No",
      masterTitle: "Mapeo maestro activo",
      masterHint: "Puerta general para cualquier entrega automática nueva por este número.",
      reportTitle: "Podcasts de reportes",
      reportHint: "Permite reportes solo si el usuario también pertenece a la audiencia de la programación.",
      dailyTitle: "Podcast diario",
      dailyHint: "Permite el resumen diario solo si el usuario está en la lista exacta de la política diaria.",
      enabled: "Habilitado",
      disabled: "Deshabilitado",
      masterSince: "Mapeo activo desde",
      reportSince: "Reportes habilitados desde",
      dailySince: "Podcast diario habilitado desde",
      eligibilityHint:
        "Un mapeo activo no basta. La audiencia, el interruptor correspondiente y sus fechas de activación se validan nuevamente en reintentos y reenvíos.",
      roleHint:
        "El rol del mapeo es una preferencia de mensajería o perfil. La audiencia de reportes usa el rol canónico de la cuenta.",
      testHint:
        "La prueba directa solo verifica proveedor y conectividad. No demuestra elegibilidad para entregas automáticas de reportes o del podcast diario.",
      testSummary: "Prueba de conectividad de AquaPortal para",
      configureAction: "Configurar entrega",
      viewAction: "Ver configuración",
      configurationTitle: "Configuración de entrega",
      configurationSubtitle: "Define la puerta maestra y los canales automáticos para este número.",
      channelsTitle: "Canales de podcasts automáticos",
      channelsHint: "Cada canal también exige que el usuario pertenezca a su audiencia correspondiente.",
      stagedHint: "Los cambios no se aplicarán hasta que guardes.",
      verificationTitle: "El estado de entrega necesita confirmación",
      verificationHint:
        "No se pudo confirmar el resultado del guardado. Recarga el destinatario antes de realizar más cambios.",
      reloadAction: "Recargar estado",
      reloadError: "No se pudo recargar el estado confirmado de entrega.",
    },
    rolesPage: {
      title: "Roles de negocio",
      subtitle: "Configura el nombre, propósito, centros predeterminados y permisos base de cada rol.",
      empty: "Sin roles disponibles.",
      filteredEmpty: "Ningún rol coincide con la búsqueda actual.",
      clearSearch: "Limpiar búsqueda",
      retry: "Reintentar",
      roleListLabel: "Roles disponibles",
      roleSelectorLabel: "Rol activo",
      canonicalRoleLabel: "Identificador del sistema",
      generalSection: "Información general",
      displayNameLabel: "Nombre visible",
      profileLabel: "Propósito operativo",
      profileHint: "Describe brevemente las responsabilidades y el alcance esperado de este rol.",
      centersLabel: "Centros predeterminados",
      centersHint: "Ingresa un centro por línea. Son valores predeterminados, no límites de autorización.",
      permissionsLabel: "Permisos base",
      availablePermissions: "Permisos disponibles",
      permissionSearchLabel: "Buscar permisos",
      permissionSearchPlaceholder: "Buscar por recurso, acción o identificador...",
      permissionSelectedCount: "{selected} de {total} seleccionados",
      permissionEmpty: "Ningún permiso coincide con la búsqueda.",
      sensitive: "Sensible",
      unavailablePermissions: "Permisos no disponibles",
      unavailablePermissionsHint:
        "Estos permisos están asignados al rol, pero no aparecen en el catálogo actual. Elimínalos antes de guardar.",
      saveRole: "Guardar cambios",
      savingRole: "Guardando...",
      saveSuccess: "Rol actualizado correctamente.",
      discardChanges: "Descartar cambios",
      unsavedChanges: "Cambios sin guardar",
      roleCount: "{count} roles",
      permissionCount: "{count} permisos",
      centerCount: "{count} centros",
      validationSummary: "Corrige los campos destacados antes de guardar.",
      displayNameError: "El nombre visible debe tener al menos 2 caracteres.",
      profileError: "El propósito operativo debe tener al menos 2 caracteres.",
      changesSummary: "Resumen de cambios",
      displayNameChanged: "Nombre visible modificado",
      profileChanged: "Propósito operativo modificado",
      centersChanged: "Centros predeterminados modificados",
      permissionsAdded: "+{count} permisos",
      permissionsRemoved: "-{count} permisos",
      permissionsHint:
        "Aquí editas la definición base del rol. El nivel de acceso de cada usuario puede modificar sus permisos finales.",
    },
    reportsPage: {
      openSchedule: "Abrir programación",
    },
    sourcesPage: {
      title: "Fuentes de reportes",
      subtitle: "Administra conexiones, programaciones e imágenes procesadas desde un solo lugar.",
      addSource: "Agregar fuente",
      integration: "Procesamiento con IA",
      integrationSubtitle: "Servicio que analiza las imágenes importadas y genera métricas e insights.",
      powerBI: "Power BI",
      sourceActionsTitle: "Importar contenido",
      sourceActionsSubtitle: "Actualiza el catálogo o agrega nuevas imágenes sin salir de esta página.",
      importPowerBI: "Importar desde Power BI",
      importImage: "Importar imagen",
      uploadImage: "Subir imagen",
      uploadTitle: "Subir imagen del reporte",
      uploadSubtitle: "Carga una imagen PNG, JPG, JPEG o WEBP desde tu equipo.",
      chooseImage: "Elegir imagen",
      selectedFiles: "Archivos seleccionados",
      selectedFile: "Archivo seleccionado",
      noFileSelected: "Ningun archivo seleccionado.",
      uploadFormats: "Formatos: PNG, JPG, JPEG, WEBP.",
      dropImages: "Arrastra imagenes aqui",
      dropImagesHint: "o usa el selector para cargar multiples archivos.",
      preview: "Vista previa",
      remove: "Quitar",
      clearSelection: "Limpiar seleccion",
      uploadSuccessSingle: "Imagen importada correctamente.",
      uploadSuccessMultiple: "Imagenes importadas correctamente.",
      uploadAndPodcastSuccessSingle: "Imagen importada y podcast regenerado correctamente.",
      uploadAndPodcastSuccessMultiple: "Imagenes importadas y podcast regenerado correctamente.",
      podcastBuildError: "La imagen se subio y proceso, pero la regeneracion del podcast fallo.",
      importTitle: "Importar imagen de Power BI",
      importSubtitle: "Selecciona un reporte y una pagina para exportarla como PNG.",
      reportLabel: "Reporte",
      pageLabel: "Pagina",
      processLabel: "Procesar con IA",
      processHint: "Guarda la imagen y ejecuta el procesamiento del reporte.",
      workspaceLabel: "Workspace",
      refreshPowerBI: "Actualizar Power BI",
      powerBIRefreshSuccess: "Catalogo Power BI actualizado: {{reports}} reportes y {{pages}} paginas.",
      powerBIReportsCount: "Reportes",
      powerBIPagesCount: "Paginas",
      powerBILastSync: "Ultima sincronizacion",
      dailyPodcastPolicyTitle: "Generación del podcast diario",
      dailyPodcastPolicySubtitle: "Define cuándo se crea el resumen diario de todos los reportes.",
      dailyPodcastPolicyStatusReady: "Al finalizar los reportes",
      dailyPodcastPolicyUnavailable: "No disponible",
      dailyPodcastPolicyModeLabel: "Momento de generación",
      dailyPodcastPolicyReadyMode: "Cuando estén listos todos los reportes",
      dailyPodcastPolicyReadyHint: "Espera a que terminen correctamente todos los reportes programados del día.",
      dailyPodcastPolicyFixedMode: "A una hora fija",
      dailyPodcastPolicyFixedHint: "Genera el podcast a la hora configurada sin esperar a los reportes pendientes.",
      dailyPodcastPolicyTimeLabel: "Hora de generación (UTC)",
      dailyPodcastPolicyTimeHint: "Usa formato de 24 horas. La hora siempre se interpreta en UTC.",
      dailyPodcastPolicyTimeRequired: "Selecciona una hora UTC válida.",
      dailyPodcastPolicyUpdatedAt: "Última actualización",
      dailyPodcastPolicyRetry: "Reintentar",
      dailyPodcastPolicySave: "Guardar configuración",
      dailyPodcastPolicySaving: "Guardando...",
      dailyPodcastPolicyUpdateSuccess: "Configuración del podcast diario actualizada correctamente.",
      dailyPolicyDeadlineLabel: "Plazo de completitud (minutos)",
      dailyPolicyDeadlineHint: "Tiempo desde el inicio de la ventana diaria antes de aplicar la política de faltantes.",
      dailyPolicyDeadlineRequired: "Ingresa un número entero entre 1 y 10.080 minutos.",
      dailyPolicyIncompleteLabel: "Al vencer el plazo con reportes faltantes",
      dailyPolicyIncompletePublishPartial: "Publicar con los reportes disponibles",
      dailyPolicyIncompleteFail: "Marcar el flujo como fallido",
      dailyPolicyIncompleteWait: "Esperar intervención de un operador",
      dailyPolicyAllowEmptyLabel: "Permitir un podcast vacío",
      dailyPolicyAllowEmptyHint: "Permite publicar aunque no haya reportes disponibles para la ventana.",
      dailyPolicyLateArrivalLabel: "Reportes que llegan tarde",
      dailyPolicyLateArrivalNext: "Incluir en el próximo podcast diario",
      dailyPolicyLateArrivalRegenerate: "Crear una revisión del podcast",
      dailyPolicyLateArrivalReview: "Solicitar revisión manual",
      schedulesTitle: "Programación de Power BI",
      schedulesSubtitle: "Automatiza la generación recurrente desde reportes y páginas de Power BI.",
      newSchedule: "Nueva programacion",
      editSchedule: "Editar",
      noSchedules: "No hay programaciones configuradas.",
      createScheduleTitle: "Nueva programacion Power BI",
      createScheduleSubtitle: "Define etiqueta, página y frecuencia de ejecución.",
      editScheduleTitle: "Editar programacion Power BI",
      editScheduleSubtitle: "Actualiza etiqueta, pagina, frecuencia y estado de esta programacion.",
      reportTypeLabel: "Etiqueta del reporte",
      reportTypePlaceholder: "Biomasa diaria",
      frequencyLabel: "Frecuencia del reporte",
      frequencyDaily: "Diaria",
      frequencyWeekly: "Semanal",
      frequencyMonthly: "Mensual",
      weeklyWeekdaysLabel: "Dias de la semana",
      weekdayMonday: "Lunes",
      weekdayTuesday: "Martes",
      weekdayWednesday: "Miercoles",
      weekdayThursday: "Jueves",
      weekdayFriday: "Viernes",
      weekdaySaturday: "Sabado",
      weekdaySunday: "Domingo",
      monthlyDayLabel: "Dia del mes",
      monthlyInvalidDayPolicyLabel: "Si el dia no existe",
      invalidDayPolicyLastDay: "Ejecutar el ultimo dia del mes",
      invalidDayPolicySkip: "Omitir ese mes",
      misfirePolicyLabel: "Ejecuciones omitidas",
      misfirePolicyLatest: "Ejecutar solo la más reciente",
      misfirePolicyCatchUp: "Recuperar ejecuciones pendientes",
      misfirePolicySkip: "Omitir ejecuciones atrasadas",
      misfirePolicyHint: "Define qué ocurre cuando el planificador vuelve a operar después de una interrupción.",
      catchUpLimitLabel: "Máximo de ejecuciones recuperadas",
      catchUpLimitHint: "Límite entre 1 y 100 cuando se recuperan ejecuciones pendientes.",
      scheduleTimeOfDayLabel: "Hora de entrega (UTC)",
      scheduleTimeOfDayHint: "Hora obligatoria en formato de 24 horas; siempre se interpreta como UTC.",
      scheduleTimeOfDayPlaceholder: "08:30",
      activeScheduleLabel: "Programacion activa",
      activeScheduleHint: "Si está activa, el backend ejecutará automáticamente esta página.",
      scheduleCreateSuccess: "Programacion guardada correctamente.",
      scheduleUpdateSuccess: "Programacion actualizada correctamente.",
      scheduleDeleteSuccess: "Programacion eliminada correctamente.",
      scheduleRunSuccess: "Programacion ejecutada correctamente.",
      scheduleRunAllSuccess: "Se ejecutaron {{count}} programaciones activas.",
      scheduleRunAllPartialError: "Se ejecutaron {{succeeded}} programaciones y {{failed}} fallaron.",
      scheduleValidationError: "Completa todos los campos obligatorios con valores validos.",
      scheduleRepairRequired: "Repara esta programacion desde Editar antes de cambiarla.",
      runNow: "Ejecutar ahora",
      runningNow: "Ejecutando...",
      runAllNow: "Ejecutar activas",
      runningAllNow: "Ejecutando activas...",
      scheduleDeleteConfirmTitle: "Eliminar programacion Power BI",
      scheduleDeleteConfirmSubtitle:
        "Esta acción eliminará la programación seleccionada y detendrá futuras ejecuciones automáticas.",
      scheduleFilterAll: "Todas",
      scheduleFilterActive: "Activas",
      scheduleFilterError: "Con error",
      scheduleFilterScheduled: "Programadas",
      scheduleFilterPaused: "Pausadas",
      scheduleSortLabel: "Orden",
      scheduleSortNextRun: "Proxima ejecucion",
      scheduleSortLastRun: "Ultima ejecucion",
      scheduleSortStatus: "Estado",
      scheduleSortReport: "Nombre del reporte",
      scheduleSortNextMaterialization: "Próxima ocurrencia",
      scheduleAutoRefresh: "Sincronización en segundo plano",
      scheduleAutoRefreshHint:
        "Comprueba cambios cada 30 segundos. La tabla permanece visible y solo se actualiza cuando los datos cambian. Selecciona para comprobar ahora.",
      scheduleRefreshError: "Error de sincronización",
      tableType: "Etiqueta",
      tableReport: "Reporte y página",
      tableReportHint: "Muestra la etiqueta operativa, el reporte de Power BI y la página seleccionada.",
      tablePage: "Pagina",
      tableSchedule: "Plan de ejecución",
      tableScheduleHint: "Frecuencia, días y hora UTC en que se ejecuta la página.",
      tableRuntime: "Estado de programación",
      tableRuntimeHint: "Muestra si la programación está activa y la próxima ocurrencia calculada por el backend.",
      tableActions: "Acciones",
      retryMinutesShort: "min",
      scheduleNever: "Nunca",
      scheduleLastRun: "Última ejecución",
      scheduleScheduledFor: "Fecha programada",
      scheduleNextAttempt: "Próxima ejecución",
      scheduleNextMaterialization: "Próxima ocurrencia",
      scheduleStatusScheduled: "Programada",
      scheduleStatusPaused: "Pausada",
      scheduleStatusSuccess: "Completada",
      scheduleStatusError: "Error",
      scheduleLastError: "Ultimo error",
      viewWorkflows: "Ver flujos",
      loadingReports: "Cargando reportes...",
      loadingPages: "Cargando paginas...",
      selectReportPlaceholder: "Selecciona un reporte",
      selectPagePlaceholder: "Selecciona una pagina",
      noReports: "Sin reportes disponibles.",
      noPages: "Sin paginas disponibles.",
      importSuccess: "Imagen importada correctamente.",
      powerBIDetailUnavailable: "Sin detalle disponible.",
      empty: "Sin fuentes.",
      emptyFiltered: "Sin coincidencias.",
      archiveTitle: "Imágenes archivadas",
      archiveSubtitle: "Revisa las capturas importadas y abre su reporte procesado.",
      archiveFilterLabel: "Filtrar imágenes",
      archiveSortLabel: "Ordenar por fecha",
      openImage: "Abrir imagen",
      noArchivedImages: "No hay imagenes archivadas.",
      allImages: "Todas",
      processedOnly: "Procesadas",
      pendingOnly: "Pendientes",
      sortNewest: "Mas nuevas",
      sortOldest: "Mas antiguas",
      sourceManual: "Manual",
      sourcePowerBI: "Power BI",
      selectImage: "Seleccionar",
      selectedCount: "seleccionadas",
      clearSelectedArchive: "Limpiar seleccion",
      selectAllVisible: "Seleccionar visibles",
      bulkDelete: "Eliminar seleccion",
      bulkDeleteConfirm: "Eliminar las imagenes seleccionadas del archivo?",
      bulkDeleteSuccess: "Imagenes seleccionadas eliminadas correctamente.",
      viewGeneratedReport: "Ver reporte generado",
      generatedReportTitle: "Reporte generado",
      generatedReportSubtitle: "Metricas e insights extraidos desde esta imagen.",
      loadingGeneratedReport: "Cargando reporte generado...",
      generatedReportEmpty: "No hay datos generados para esta imagen todavia.",
      summaryTitle: "Resumen",
      podcastTitle: "Podcast generado",
      podcastScriptTitle: "Texto del podcast",
      loadingPodcastAudio: "Cargando audio del podcast...",
      podcastAudioUnavailable: "No se pudo cargar el audio del podcast.",
      podcastScriptUnavailable: "No hay texto de podcast disponible para este reporte.",
      podcastAvailable: "Podcast disponible",
      insightsTitle: "Insights",
      metricsTitle: "Metricas",
      metricName: "Metrica",
      metricValue: "Valor",
      audienceLabel: "Audiencia",
      recommendationLabel: "Recomendacion",
      cageLabel: "Jaula",
      priorityLabel: "Prioridad",
      previousReport: "Anterior",
      nextReport: "Siguiente",
      exportPdf: "Exportar PDF",
      viewFullscreen: "Pantalla completa",
      deleteImage: "Eliminar",
      deleting: "Eliminando...",
      deleteConfirm: "Eliminar esta imagen del reporte del archivo?",
      deleteSuccess: "Imagen eliminada correctamente.",
      sourceLabel: "Fuente",
      statusLabel: "Estado",
      centerLabel: "Centro",
      processed: "Procesado",
      updated: "Actualizado",
      files: "archivos",
      assignedRoles: "ROLES ASIGNADOS",
      image: "Imagen",
      file: "Archivo",
      workflowOwned: "Publicado por flujo",
      viewWorkflow: "Ver flujo",
      immutableRecord: "Este registro pertenece a un flujo publicado y no se puede modificar.",
    },
    workflowsPage: {
      title: "Flujos de trabajo",
      subtitle: "Supervisa importaciones, generación de podcasts, reintentos y entregas.",
      backToList: "Volver a flujos",
      refresh: "Actualizar",
      loadMore: "Cargar más",
      kindLabel: "Tipo",
      statusLabel: "Estado",
      allKinds: "Todos los tipos",
      allStatuses: "Todos los estados",
      kindReport: "Reporte",
      kindDaily: "Podcast diario",
      statusPending: "Pendiente",
      statusActive: "Activo",
      statusWaiting: "Esperando",
      statusRetryWait: "Reintento programado",
      statusPublished: "Publicado",
      statusFailed: "Fallo terminal",
      statusCancelled: "Cancelado",
      stageStatusRunning: "En ejecución",
      stageStatusSucceeded: "Completada",
      stageStatusSkipped: "Omitida",
      attemptLostLease: "Lease perdido",
      deliveryPending: "Pendiente",
      deliverySending: "Enviando",
      deliveryRetryWait: "Reintento programado",
      deliverySent: "Enviado",
      deliveryFailed: "Fallo terminal",
      eventReportPublished: "Reporte publicado",
      eventDailyPodcastPublished: "Podcast diario publicado",
      noWorkflows: "No hay flujos para estos filtros.",
      occurrence: "Ocurrencia",
      reportRun: "Ejecución de reporte",
      runId: "ID de ejecución",
      occurrenceKey: "Clave de ocurrencia",
      currentStage: "Etapa actual",
      nextAction: "Próxima acción",
      createdAt: "Creado",
      updatedAt: "Actualizado",
      publishedAt: "Publicado",
      trigger: "Disparador",
      configuration: "Configuración capturada",
      stages: "Etapas",
      attempts: "Intentos",
      audits: "Auditoría",
      delivery: "Entrega",
      noAttempts: "Aún no hay intentos.",
      noAudits: "Aún no hay eventos de auditoría.",
      noDelivery: "No hay eventos de entrega.",
      retry: "Reintentar flujo",
      cancel: "Cancelar flujo",
      rerunStage: "Reejecutar etapa",
      retryDelivery: "Reintentar entrega",
      reasonLabel: "Motivo del operador",
      reasonPlaceholder: "Describe por qué se realiza esta acción.",
      reasonRequired: "Ingresa un motivo de 1 a 1.000 caracteres.",
      chooseStage: "Etapa a reejecutar",
      rerunWarning: "Esta etapa y todos sus checkpoints posteriores serán invalidados.",
      confirmAction: "Confirmar acción",
      provider: "Proveedor",
      model: "Modelo",
      attemptCount: "Intentos",
      failureCount: "Fallos",
      nextAttempt: "Próximo intento",
      checkpoint: "Checkpoint",
      dependencies: "Dependencias",
      outcome: "Resultado",
      actor: "Actor",
      action: "Acción",
      details: "Detalles",
      eventType: "Tipo de evento",
      publication: "Publicación",
      publicRecords: "Registros públicos",
      safeFailure: "Último fallo",
      publishedDeliveryNote: "La publicación finalizó correctamente. La entrega se controla por separado.",
      waitingNote: "El flujo espera progreso externo; esto no es un fallo.",
      retryWaitNote: "El flujo volverá a intentar automáticamente en el horario indicado.",
      batchSummary: "{{accepted}} flujos aceptados; {{failed}} solicitudes fallaron.",
      batchUnknown: "{{unknown}} solicitudes tienen resultado incierto por un error de red.",
      stagePowerBIExportStart: "Iniciar exportación de Power BI",
      stagePowerBIExportWait: "Esperar exportación de Power BI",
      stagePowerBIExportDownload: "Descargar exportación de Power BI",
      stageCollectDailyInputs: "Recolectar entradas diarias",
      stageExtractReportVisualData: "Extraer datos visuales de Power BI",
      stageAssembleReportContext: "Ensamblar contexto del reporte",
      stageAnalyzeReportContext: "Analizar reporte en contexto",
      stageGenerateReportScript: "Generar narración del reporte",
      stageAssembleDailyContext: "Ensamblar contexto diario",
      stageSynthesizeDailyBrief: "Sintetizar resumen entre reportes",
      stageGenerateDailyScript: "Generar narración diaria",
      stageSynthesizeAudio: "Sintetizar audio",
      stageNormalizeAudio: "Normalizar audio",
      stageRenderVideo: "Renderizar video",
      stagePublish: "Publicar",
      republishDaily: "Volver a publicar (sin WhatsApp)",
      republishDailyHint:
        "Actualiza el episodio, la publicación y la base de conocimiento sin enviar notificaciones. Solo para podcasts diarios ya publicados.",
      republishDailyWithRag: "Forzar nueva carga en RAG",
      republishDailyWithRagHint:
        "Vuelve a publicar y carga de nuevo el documento en la base de conocimiento, aunque ya se haya cargado antes.",
      republishResultTitle: "Episodio diario actualizado",
      republishResultSkippedWhatsapp: "Notificaciones de WhatsApp omitidas.",
      republishResultQueuedWhatsapp: "Notificaciones de WhatsApp en cola.",
      republishResultRagAttempted:
        "Se intentó cargar el documento en RAG. La respuesta no confirma que la carga haya finalizado correctamente.",
      republishResultRagSkipped: "No se realizó la carga en RAG.",
      republishResultRagAlreadyUploaded: "Carga en RAG omitida: el documento ya se había cargado correctamente.",
      republishResultRagDisabled: "Carga en RAG omitida: el servicio está desactivado.",
      republishError: "No se pudo completar la publicación. Actualiza el episodio antes de volver a intentarlo.",
      podcastProvenanceMissing: "Sin vínculo al flujo de trabajo",
    },
    usagePage: {
      title: "Registro de uso",
      subtitle: "Historial de deliveries y auditoria de envios.",
      delivered: "Entregado",
      pending: "Pendiente",
      error: "Error",
      filters: "Filtros",
      allUsers: "Usuario: Todos",
      allStatuses: "Estado: Todos",
      clearFilters: "Limpiar filtros",
      deliveries: "Entregas",
      empty: "Sin registros.",
      tableUser: "USUARIO",
      tableDateTime: "FECHA Y HORA",
      tableReport: "REPORTE",
      tableStatus: "ESTADO",
      tableRecipient: "DESTINATARIO",
      tableChannel: "CANAL",
      tableTrigger: "TRIGGER",
      tableActions: "ACCIONES",
      messageWithMedia: "Mensaje con media",
      message: "Mensaje",
      testTag: "TEST",
      reportId: "Reporte",
      noReportId: "Sin reporte asociado",
      unknownChannel: "whatsapp",
      unknownTrigger: "manual",
      withMediaCount: "{{count}} media",
      deleteConfirm: "Eliminar este registro de delivery?",
      deleteSuccess: "Delivery eliminado correctamente.",
      deleting: "Eliminando...",
      deleteAll: "Eliminar todo",
      deleteAllConfirm: "Eliminar todo el registro de uso?",
      deleteAllSuccess: "Registro de uso eliminado correctamente.",
      deletingAll: "Eliminando todo...",
      resetResend: "Resetear y reenviar",
      resetResendConfirm: "Resetear already sent y reenviar este reporte por WhatsApp?",
      resetResendSuccess: "Reporte reenviado correctamente.",
      resettingResend: "Reenviando...",
      resetResendAll: "Resetear y reenviar todo",
      resetResendAllConfirm: "Resetear already sent y reenviar todos los reportes listos por WhatsApp?",
      resetResendAllSuccess: "Reportes listos reenviados correctamente.",
      resettingResendAll: "Reenviando todo...",
    },
    whatsappPage: {
      title: "Destinatarios y envíos por WhatsApp",
      subtitle:
        "Configura las puertas de entrega por destinatario y revisa los envíos registrados esta semana. Un mapeo activo por sí solo no recibe podcasts automáticos.",
      addRecipient: "Agregar destinatario",
      weeklyTitle: "Envíos de esta semana",
      weeklySubtitle: "Consulta los intentos de envío registrados por día.",
      viewFullLog: "Ver registro completo",
      noDeliveries: "No hay envíos registrados para este día.",
      deliveriesLoadingError: "No se pudieron cargar los envíos de esta semana.",
      deliverySingular: "envío",
      deliveryPlural: "envíos",
      deliveryListLabel: "Envíos del día seleccionado",
      recentDeliveriesNotice: "Mostrando los 5 envíos más recientes de este día.",
      contentReport: "Reporte",
      contentDailyPodcast: "Resumen diario",
      noContentReference: "Sin referencia",
      sourceAutomation: "Automatización",
      sourceReportReady: "Reporte listo",
      sourceDailyReady: "Resumen diario listo",
      sourceManual: "Manual",
      sourceManualResend: "Reenvío manual",
      sourceBulkResend: "Reenvío masivo",
      sourceUnknown: "Sin identificar",
      statusProcessing: "En proceso",
      statusSent: "Enviado",
      statusDelivered: "Entregado",
      statusRead: "Leído",
      statusError: "Error",
      recipientsTitle: "Destinatarios configurados",
      recipientsSubtitle:
        "Vincula cada número con un usuario. El rol solo adapta mensajería; la elegibilidad automática también exige audiencia y los interruptores correspondientes.",
      emptyRecipients: "Aún no hay destinatarios configurados.",
      noSearchResults: "No se encontraron destinatarios para esta búsqueda.",
      recipientSingular: "destinatario",
      recipientPlural: "destinatarios",
      tableUser: "DESTINATARIO",
      tablePhone: "NÚMERO DE WHATSAPP",
      tableRole: "ROL ASIGNADO",
      tableActivation: "ÚLTIMA ACTIVACIÓN",
      tableStatus: "ESTADO",
      tableActions: "ACCIONES",
      inheritRole: "Usar el rol del usuario",
      roleCeo: "CEO",
      roleProductionManager: "Gerente de producción",
      roleCenterManager: "Gerente de centro",
      roleVeterinarian: "Veterinario/a",
      roleQualityManager: "Gerente de calidad",
      roleOperations: "Operaciones",
      roleFinance: "Finanzas",
      createTitle: "Agregar destinatario",
      createSubtitle: "Vincula un número de WhatsApp con un usuario y configura su disponibilidad para futuros envíos.",
      editTitle: "Editar destinatario",
      editSubtitle: "Actualiza el rol asignado y el estado de este destinatario.",
      fieldUser: "Usuario",
      fieldPhone: "Número de WhatsApp",
      fieldRole: "Rol de mensajería (opcional)",
      activeTitle: "Destinatario activo",
      activeSubtitle: "Es la puerta maestra; los podcasts de reportes y diarios siguen siendo opt-in independientes.",
      activeStatus: "Activo",
      inactiveStatus: "Inactivo",
      enableRecipient: "Activar destinatario",
      disableRecipient: "Pausar destinatario",
      editAction: "Editar",
      deleteAction: "Eliminar",
      createAction: "Agregar destinatario",
      saveChanges: "Guardar cambios",
      deleteConfirm:
        "¿Eliminar este destinatario? Dejará de recibir contenido, pero su historial de envíos se conservará.",
      monday: "Lunes",
      tuesday: "Martes",
      wednesday: "Miércoles",
      thursday: "Jueves",
      friday: "Viernes",
      saturday: "Sábado",
      sunday: "Domingo",
    },
  },
  en: {
    common: {
      cancel: "Cancel",
      save: "Save",
      edit: "Edit",
      delete: "Delete",
      active: "Active",
      inactive: "Inactive",
      role: "Role",
      name: "Name",
      email: "Email",
      user: "User",
      phone: "Phone",
      center: "Center",
      status: "Status",
      eligibleSince: "Eligible since",
      loadingError: "Could not load data.",
      providerFailure: "The provider could not complete the request.",
      serviceUnavailable: "The service is unavailable or not configured.",
      requestTimeout: "The provider request timed out.",
      createError: "Could not create item.",
      updateError: "Could not update item.",
      deleteError: "Could not delete item.",
      noResults: "No matches found.",
      from: "From",
      to: "To",
      searchPlaceholder: "Search by name or content...",
      noData: "No data.",
    },
    shell: {
      appName: "miVocero",
      appSubtitle: "Voice reports",
      users: "Users",
      roles: "Roles",
      sources: "Sources",
      workflows: "Workflows",
      whatsapp: "WhatsApp",
      usageLog: "Usage log",
      activeChannel: "Active channel",
      ready: "Ready",
      notReady: "Not ready",
      signOut: "Sign out",
      fallbackUserName: "Admin",
      collapseSidebar: "Collapse sidebar",
      expandSidebar: "Expand sidebar",
      openNavigation: "Open navigation",
      closeNavigation: "Close navigation",
      navigationLabel: "Main navigation",
      notifications: "Notifications",
      openNotifications: "Open notifications",
      noNotifications: "No notifications",
      noNotificationsDescription: "Updates for your account will appear here.",
      openUserMenu: "Open user menu",
      account: "Account",
    },
    signIn: {
      brandTitle: "Vocero Admin",
      brandSubtitle: "Administrative access",
      title: "Sign in",
      subtitle: "Select a demo profile from the backend.",
      noProfiles: "No demo profiles.",
      profile: "Profile",
      password: "Password",
      passwordPlaceholder: "Password",
      invalidPassword: "Invalid password.",
      loadError: "Could not load data.",
      submitError: "Could not sign in.",
      permissions: "Permissions",
      submit: "Sign in",
    },
    usersPage: {
      title: "Users",
      subtitle: "Manage who receives the strategic reports.",
      newUser: "New user",
      empty: "No users.",
      userCountLabel: "User",
      tableUser: "USER",
      tablePhone: "PHONE",
      tableRole: "ROLE",
      tableBusinessRole: "BUSINESS ROLE",
      tableStatus: "STATUS",
      editUser: "Edit user",
      deleteUser: "Delete user",
      createTitle: "New user",
      createSubtitle: "Create a profile and assign a role.",
      editTitle: "Edit user",
      accessLevel: "Access",
      accessRead: "Read only",
      accessReadWrite: "Read + write",
      accessAdmin: "Admin",
      deleteConfirm: "Delete this user?",
      deleteSuccess: "User deleted successfully.",
      whatsAppMappings: "WhatsApp mappings",
      whatsAppMappingsSubtitle:
        "Associate numbers and configure the master mapping, report podcasts, and daily podcasts independently.",
      addWhatsAppMapping: "Add number",
      noWhatsAppMappings: "This user has no mapped numbers.",
      saveWhatsAppMapping: "Save number",
      deleteWhatsAppConfirm: "Delete this WhatsApp mapping?",
      morePhones: "more",
      testWhatsApp: "Test connection",
      testWhatsAppSuccess: "Connectivity test requested. Check the delivery status.",
    },
    whatsappDelivery: {
      summaryLabel: "Delivery status",
      masterShort: "Master",
      reportShort: "Reports",
      dailyShort: "Daily",
      unknownShort: "Unconfirmed",
      onShort: "On",
      offShort: "Off",
      masterTitle: "Master mapping active",
      masterHint: "The general gate for any new automatic delivery to this number.",
      reportTitle: "Report podcasts",
      reportHint: "Allows reports only when the user is also in the source schedule's audience.",
      dailyTitle: "Daily podcast",
      dailyHint: "Allows the daily briefing only when the user is in the daily policy's exact allow list.",
      enabled: "Enabled",
      disabled: "Disabled",
      masterSince: "Mapping active since",
      reportSince: "Reports enabled since",
      dailySince: "Daily podcast enabled since",
      eligibilityHint:
        "An active mapping is not enough. Audience membership, the matching switch, and activation times are re-evaluated for retries and resends.",
      roleHint:
        "The mapping role is a messaging or profile override. Report audiences use the account's canonical role.",
      testHint:
        "A direct test checks only provider connectivity. It does not prove eligibility for automatic report or daily podcast delivery.",
      testSummary: "AquaPortal connectivity test for",
      configureAction: "Configure delivery",
      viewAction: "View settings",
      configurationTitle: "Delivery settings",
      configurationSubtitle: "Set the master gate and automatic channels for this number.",
      channelsTitle: "Automatic podcast channels",
      channelsHint: "Each channel also requires the user to belong to its matching audience.",
      stagedHint: "Changes are not applied until you save.",
      verificationTitle: "Delivery status needs confirmation",
      verificationHint: "The save result could not be confirmed. Reload the recipient before making more changes.",
      reloadAction: "Reload status",
      reloadError: "Could not reload the confirmed delivery status.",
    },
    rolesPage: {
      title: "Business roles",
      subtitle: "Configure the name, purpose, default centers, and base permissions for each role.",
      empty: "No roles available.",
      filteredEmpty: "No roles match the current search.",
      clearSearch: "Clear search",
      retry: "Retry",
      roleListLabel: "Available roles",
      roleSelectorLabel: "Active role",
      canonicalRoleLabel: "System identifier",
      generalSection: "General information",
      displayNameLabel: "Display name",
      profileLabel: "Operational purpose",
      profileHint: "Briefly describe this role's expected responsibilities and scope.",
      centersLabel: "Default centers",
      centersHint: "Enter one center per line. These are defaults, not authorization boundaries.",
      permissionsLabel: "Base permissions",
      availablePermissions: "Available permissions",
      permissionSearchLabel: "Search permissions",
      permissionSearchPlaceholder: "Search by resource, action, or identifier...",
      permissionSelectedCount: "{selected} of {total} selected",
      permissionEmpty: "No permissions match the search.",
      sensitive: "Sensitive",
      unavailablePermissions: "Unavailable permissions",
      unavailablePermissionsHint:
        "These permissions are assigned to the role but are absent from the current catalog. Remove them before saving.",
      saveRole: "Save changes",
      savingRole: "Saving...",
      saveSuccess: "Role updated successfully.",
      discardChanges: "Discard changes",
      unsavedChanges: "Unsaved changes",
      roleCount: "{count} roles",
      permissionCount: "{count} permissions",
      centerCount: "{count} centers",
      validationSummary: "Resolve the highlighted fields before saving.",
      displayNameError: "The display name must contain at least 2 characters.",
      profileError: "The operational purpose must contain at least 2 characters.",
      changesSummary: "Change summary",
      displayNameChanged: "Display name changed",
      profileChanged: "Operational purpose changed",
      centersChanged: "Default centers changed",
      permissionsAdded: "+{count} permissions",
      permissionsRemoved: "-{count} permissions",
      permissionsHint:
        "This page edits the role's base definition. Each user's access level can alter their final permissions.",
    },
    reportsPage: {
      openSchedule: "Open schedule",
    },
    sourcesPage: {
      title: "Report sources",
      subtitle: "Manage connections, schedules, and processed images from one place.",
      addSource: "Add source",
      integration: "AI processing",
      integrationSubtitle: "Service that analyzes imported images and generates metrics and insights.",
      powerBI: "Power BI",
      sourceActionsTitle: "Import content",
      sourceActionsSubtitle: "Refresh the catalog or add new images without leaving this page.",
      importPowerBI: "Import from Power BI",
      importImage: "Import image",
      uploadImage: "Upload image",
      uploadTitle: "Upload report image",
      uploadSubtitle: "Upload a PNG, JPG, JPEG, or WEBP image from your device.",
      chooseImage: "Choose image",
      selectedFiles: "Selected files",
      selectedFile: "Selected file",
      noFileSelected: "No file selected.",
      uploadFormats: "Formats: PNG, JPG, JPEG, WEBP.",
      dropImages: "Drop images here",
      dropImagesHint: "or use the picker to upload multiple files.",
      preview: "Preview",
      remove: "Remove",
      clearSelection: "Clear selection",
      uploadSuccessSingle: "Image imported successfully.",
      uploadSuccessMultiple: "Images imported successfully.",
      uploadAndPodcastSuccessSingle: "Image uploaded and podcast regenerated successfully.",
      uploadAndPodcastSuccessMultiple: "Images uploaded and podcast regenerated successfully.",
      podcastBuildError: "The image upload and processing succeeded, but podcast regeneration failed.",
      importTitle: "Import Power BI image",
      importSubtitle: "Select a report and page to export it as a PNG.",
      reportLabel: "Report",
      pageLabel: "Page",
      processLabel: "Process with AI",
      processHint: "Store the image and run report processing.",
      workspaceLabel: "Workspace",
      refreshPowerBI: "Refresh Power BI",
      powerBIRefreshSuccess: "Power BI catalog refreshed: {{reports}} reports and {{pages}} pages.",
      powerBIReportsCount: "Reports",
      powerBIPagesCount: "Pages",
      powerBILastSync: "Last sync",
      dailyPodcastPolicyTitle: "Daily podcast generation",
      dailyPodcastPolicySubtitle: "Choose when to create the daily summary of all reports.",
      dailyPodcastPolicyStatusReady: "After reports finish",
      dailyPodcastPolicyUnavailable: "Unavailable",
      dailyPodcastPolicyModeLabel: "Generation timing",
      dailyPodcastPolicyReadyMode: "When all reports are ready",
      dailyPodcastPolicyReadyHint: "Wait for every scheduled report for the day to finish successfully.",
      dailyPodcastPolicyFixedMode: "At a fixed time",
      dailyPodcastPolicyFixedHint:
        "Generate the podcast at the configured time without waiting for outstanding reports.",
      dailyPodcastPolicyTimeLabel: "Generation time (UTC)",
      dailyPodcastPolicyTimeHint: "Use 24-hour time. The value is always interpreted as UTC.",
      dailyPodcastPolicyTimeRequired: "Select a valid UTC time.",
      dailyPodcastPolicyUpdatedAt: "Last updated",
      dailyPodcastPolicyRetry: "Try again",
      dailyPodcastPolicySave: "Save configuration",
      dailyPodcastPolicySaving: "Saving...",
      dailyPodcastPolicyUpdateSuccess: "Daily podcast configuration updated successfully.",
      dailyPolicyDeadlineLabel: "Completeness deadline (minutes)",
      dailyPolicyDeadlineHint: "Time from the daily window start before the incomplete-input policy applies.",
      dailyPolicyDeadlineRequired: "Enter a whole number from 1 to 10,080 minutes.",
      dailyPolicyIncompleteLabel: "When the deadline passes with missing reports",
      dailyPolicyIncompletePublishPartial: "Publish available reports",
      dailyPolicyIncompleteFail: "Fail the workflow",
      dailyPolicyIncompleteWait: "Wait for an operator",
      dailyPolicyAllowEmptyLabel: "Allow an empty podcast",
      dailyPolicyAllowEmptyHint: "Allow publication when no reports are available for the window.",
      dailyPolicyLateArrivalLabel: "Late report arrivals",
      dailyPolicyLateArrivalNext: "Include in the next daily podcast",
      dailyPolicyLateArrivalRegenerate: "Create a podcast revision",
      dailyPolicyLateArrivalReview: "Require manual review",
      schedulesTitle: "Power BI scheduling",
      schedulesSubtitle: "Automate recurring generation from Power BI reports and pages.",
      newSchedule: "New schedule",
      editSchedule: "Edit",
      noSchedules: "No schedules configured.",
      createScheduleTitle: "New Power BI schedule",
      createScheduleSubtitle: "Define the report label, page, and run frequency.",
      editScheduleTitle: "Edit Power BI schedule",
      editScheduleSubtitle: "Update the report label, page, frequency, and state for this schedule.",
      reportTypeLabel: "Report label",
      reportTypePlaceholder: "Daily biomass",
      frequencyLabel: "Report frequency",
      frequencyDaily: "Daily",
      frequencyWeekly: "Weekly",
      frequencyMonthly: "Monthly",
      weeklyWeekdaysLabel: "Weekdays",
      weekdayMonday: "Monday",
      weekdayTuesday: "Tuesday",
      weekdayWednesday: "Wednesday",
      weekdayThursday: "Thursday",
      weekdayFriday: "Friday",
      weekdaySaturday: "Saturday",
      weekdaySunday: "Sunday",
      monthlyDayLabel: "Day of month",
      monthlyInvalidDayPolicyLabel: "When the day does not exist",
      invalidDayPolicyLastDay: "Run on the last day of the month",
      invalidDayPolicySkip: "Skip that month",
      misfirePolicyLabel: "Missed occurrences",
      misfirePolicyLatest: "Run only the latest",
      misfirePolicyCatchUp: "Catch up missed occurrences",
      misfirePolicySkip: "Skip overdue occurrences",
      misfirePolicyHint: "Controls what happens when scheduling resumes after an interruption.",
      catchUpLimitLabel: "Maximum catch-up occurrences",
      catchUpLimitHint: "A limit from 1 to 100 when catching up missed occurrences.",
      scheduleTimeOfDayLabel: "Delivery time (UTC)",
      scheduleTimeOfDayHint: "Required 24-hour time; it is always interpreted as UTC.",
      scheduleTimeOfDayPlaceholder: "08:30",
      activeScheduleLabel: "Schedule enabled",
      activeScheduleHint: "When enabled, the backend runs this page automatically.",
      scheduleCreateSuccess: "Schedule saved successfully.",
      scheduleUpdateSuccess: "Schedule updated successfully.",
      scheduleDeleteSuccess: "Schedule deleted successfully.",
      scheduleRunSuccess: "Schedule executed successfully.",
      scheduleRunAllSuccess: "{{count}} active schedules executed.",
      scheduleRunAllPartialError: "{{succeeded}} schedules executed and {{failed}} failed.",
      scheduleValidationError: "Complete every required field with a valid value.",
      scheduleRepairRequired: "Repair this schedule from Edit before changing it.",
      runNow: "Run now",
      runningNow: "Running...",
      runAllNow: "Run active",
      runningAllNow: "Running active...",
      scheduleDeleteConfirmTitle: "Delete Power BI schedule",
      scheduleDeleteConfirmSubtitle: "This removes the selected schedule and stops future automatic runs.",
      scheduleFilterAll: "All",
      scheduleFilterActive: "Active",
      scheduleFilterError: "Errors",
      scheduleFilterScheduled: "Scheduled",
      scheduleFilterPaused: "Paused",
      scheduleSortLabel: "Sort",
      scheduleSortNextRun: "Next run",
      scheduleSortLastRun: "Last run",
      scheduleSortStatus: "Status",
      scheduleSortReport: "Report name",
      scheduleSortNextMaterialization: "Next occurrence",
      scheduleAutoRefresh: "Background sync",
      scheduleAutoRefreshHint:
        "Checks for changes every 30 seconds. The table stays visible and updates only when data changes. Select to check now.",
      scheduleRefreshError: "Sync unavailable",
      tableType: "Label",
      tableReport: "Report and page",
      tableReportHint: "Shows the operational label, Power BI report, and selected page.",
      tablePage: "Page",
      tableSchedule: "Run plan",
      tableScheduleHint: "Frequency, days, and UTC time when the page runs.",
      tableRuntime: "Schedule state",
      tableRuntimeHint: "Shows whether the schedule is active and its next backend-calculated occurrence.",
      tableActions: "Actions",
      retryMinutesShort: "min",
      scheduleNever: "Never",
      scheduleLastRun: "Last run",
      scheduleScheduledFor: "Scheduled date",
      scheduleNextAttempt: "Next run",
      scheduleNextMaterialization: "Next occurrence",
      scheduleStatusScheduled: "Scheduled",
      scheduleStatusPaused: "Paused",
      scheduleStatusSuccess: "Successful",
      scheduleStatusError: "Error",
      scheduleLastError: "Last error",
      viewWorkflows: "View workflows",
      loadingReports: "Loading reports...",
      loadingPages: "Loading pages...",
      selectReportPlaceholder: "Select a report",
      selectPagePlaceholder: "Select a page",
      noReports: "No reports available.",
      noPages: "No pages available.",
      importSuccess: "Image imported successfully.",
      powerBIDetailUnavailable: "No details available.",
      empty: "No sources.",
      emptyFiltered: "No matches found.",
      archiveTitle: "Archived images",
      archiveSubtitle: "Review imported captures and open their processed report.",
      archiveFilterLabel: "Filter images",
      archiveSortLabel: "Sort by date",
      openImage: "Open image",
      noArchivedImages: "No archived images.",
      allImages: "All",
      processedOnly: "Processed",
      pendingOnly: "Pending",
      sortNewest: "Newest",
      sortOldest: "Oldest",
      sourceManual: "Manual",
      sourcePowerBI: "Power BI",
      selectImage: "Select",
      selectedCount: "selected",
      clearSelectedArchive: "Clear selection",
      selectAllVisible: "Select visible",
      bulkDelete: "Delete selected",
      bulkDeleteConfirm: "Delete the selected images from the archive?",
      bulkDeleteSuccess: "Selected images deleted successfully.",
      viewGeneratedReport: "View generated report",
      generatedReportTitle: "Generated report",
      generatedReportSubtitle: "Metrics and insights extracted from this image.",
      loadingGeneratedReport: "Loading generated report...",
      generatedReportEmpty: "No generated data is available for this image yet.",
      summaryTitle: "Summary",
      podcastTitle: "Generated podcast",
      podcastScriptTitle: "Podcast script",
      loadingPodcastAudio: "Loading podcast audio...",
      podcastAudioUnavailable: "Could not load podcast audio.",
      podcastScriptUnavailable: "No podcast script is available for this report.",
      podcastAvailable: "Podcast available",
      insightsTitle: "Insights",
      metricsTitle: "Metrics",
      metricName: "Metric",
      metricValue: "Value",
      audienceLabel: "Audience",
      recommendationLabel: "Recommendation",
      cageLabel: "Cage",
      priorityLabel: "Priority",
      previousReport: "Previous",
      nextReport: "Next",
      exportPdf: "Export PDF",
      viewFullscreen: "Fullscreen",
      deleteImage: "Delete",
      deleting: "Deleting...",
      deleteConfirm: "Delete this report image from the archive?",
      deleteSuccess: "Image deleted successfully.",
      sourceLabel: "Source",
      statusLabel: "Status",
      centerLabel: "Center",
      processed: "Processed",
      updated: "Updated",
      files: "files",
      assignedRoles: "ASSIGNED ROLES",
      image: "Image",
      file: "File",
      workflowOwned: "Workflow published",
      viewWorkflow: "View workflow",
      immutableRecord: "This record belongs to a published workflow and cannot be changed.",
    },
    workflowsPage: {
      title: "Workflows",
      subtitle: "Monitor report runs, podcast generation, retries, and deliveries.",
      backToList: "Back to workflows",
      refresh: "Refresh",
      loadMore: "Load more",
      kindLabel: "Kind",
      statusLabel: "Status",
      allKinds: "All kinds",
      allStatuses: "All statuses",
      kindReport: "Report",
      kindDaily: "Daily podcast",
      statusPending: "Pending",
      statusActive: "Active",
      statusWaiting: "Waiting",
      statusRetryWait: "Retry scheduled",
      statusPublished: "Published",
      statusFailed: "Terminal failure",
      statusCancelled: "Cancelled",
      stageStatusRunning: "Running",
      stageStatusSucceeded: "Succeeded",
      stageStatusSkipped: "Skipped",
      attemptLostLease: "Lost lease",
      deliveryPending: "Pending",
      deliverySending: "Sending",
      deliveryRetryWait: "Retry scheduled",
      deliverySent: "Sent",
      deliveryFailed: "Terminal failure",
      eventReportPublished: "Report published",
      eventDailyPodcastPublished: "Daily podcast published",
      noWorkflows: "No workflows match these filters.",
      occurrence: "Occurrence",
      reportRun: "Report run",
      runId: "Run ID",
      occurrenceKey: "Occurrence key",
      currentStage: "Current stage",
      nextAction: "Next action",
      createdAt: "Created",
      updatedAt: "Updated",
      publishedAt: "Published",
      trigger: "Trigger",
      configuration: "Configuration snapshot",
      stages: "Stages",
      attempts: "Attempts",
      audits: "Audit events",
      delivery: "Delivery",
      noAttempts: "No attempts yet.",
      noAudits: "No audit events yet.",
      noDelivery: "No delivery events.",
      retry: "Retry workflow",
      cancel: "Cancel workflow",
      rerunStage: "Rerun stage",
      retryDelivery: "Retry delivery",
      reasonLabel: "Operator reason",
      reasonPlaceholder: "Describe why this action is being taken.",
      reasonRequired: "Enter a reason from 1 to 1,000 characters.",
      chooseStage: "Stage to rerun",
      rerunWarning: "This stage and every downstream checkpoint will be invalidated.",
      confirmAction: "Confirm action",
      provider: "Provider",
      model: "Model",
      attemptCount: "Attempts",
      failureCount: "Failures",
      nextAttempt: "Next attempt",
      checkpoint: "Checkpoint",
      dependencies: "Dependencies",
      outcome: "Outcome",
      actor: "Actor",
      action: "Action",
      details: "Details",
      eventType: "Event type",
      publication: "Publication",
      publicRecords: "Public records",
      safeFailure: "Last failure",
      publishedDeliveryNote: "Publication completed successfully. Delivery is tracked separately.",
      waitingNote: "The workflow is waiting for external progress; this is not a failure.",
      retryWaitNote: "The workflow will retry automatically at the scheduled time.",
      batchSummary: "{{accepted}} workflows accepted; {{failed}} requests failed.",
      batchUnknown: "{{unknown}} requests have an uncertain result because of a network error.",
      stagePowerBIExportStart: "Start Power BI export",
      stagePowerBIExportWait: "Wait for Power BI export",
      stagePowerBIExportDownload: "Download Power BI export",
      stageCollectDailyInputs: "Collect daily inputs",
      stageExtractReportVisualData: "Extract Power BI visual data",
      stageAssembleReportContext: "Assemble report context",
      stageAnalyzeReportContext: "Analyze report in context",
      stageGenerateReportScript: "Generate report narration",
      stageAssembleDailyContext: "Assemble daily context",
      stageSynthesizeDailyBrief: "Synthesize cross-report brief",
      stageGenerateDailyScript: "Generate daily narration",
      stageSynthesizeAudio: "Synthesize audio",
      stageNormalizeAudio: "Normalize audio",
      stageRenderVideo: "Render video",
      stagePublish: "Publish",
      republishDaily: "Republish (no WhatsApp)",
      republishDailyHint:
        "Refresh the episode, publication and knowledge base without sending notifications. Only available for published daily podcasts.",
      republishDailyWithRag: "Force RAG re-upload",
      republishDailyWithRagHint:
        "Republish and upload the document to the knowledge base again, even if it was already uploaded.",
      republishResultTitle: "Daily episode updated",
      republishResultSkippedWhatsapp: "WhatsApp notifications were skipped.",
      republishResultQueuedWhatsapp: "WhatsApp notifications are queued.",
      republishResultRagAttempted: "RAG upload was attempted. The response does not confirm that the upload succeeded.",
      republishResultRagSkipped: "RAG upload was skipped.",
      republishResultRagAlreadyUploaded: "RAG upload skipped: the document was already uploaded successfully.",
      republishResultRagDisabled: "RAG upload skipped: the service is disabled.",
      republishError: "Republishing could not be completed. Refresh the episode before trying again.",
      podcastProvenanceMissing: "Missing workflow link",
    },
    usagePage: {
      title: "Usage log",
      subtitle: "Delivery and audit history.",
      delivered: "Delivered",
      pending: "Pending",
      error: "Error",
      filters: "Filters",
      allUsers: "User: All",
      allStatuses: "Status: All",
      clearFilters: "Clear filters",
      deliveries: "Deliveries",
      empty: "No records.",
      tableUser: "USER",
      tableDateTime: "DATE & TIME",
      tableReport: "REPORT",
      tableStatus: "STATUS",
      tableRecipient: "RECIPIENT",
      tableChannel: "CHANNEL",
      tableTrigger: "TRIGGER",
      tableActions: "ACTIONS",
      messageWithMedia: "Message with media",
      message: "Message",
      testTag: "TEST",
      reportId: "Report",
      noReportId: "No linked report",
      unknownChannel: "whatsapp",
      unknownTrigger: "manual",
      withMediaCount: "{{count}} media",
      deleteConfirm: "Delete this delivery record?",
      deleteSuccess: "Delivery deleted successfully.",
      deleting: "Deleting...",
      deleteAll: "Delete all",
      deleteAllConfirm: "Delete the entire usage log?",
      deleteAllSuccess: "Usage log deleted successfully.",
      deletingAll: "Deleting all...",
      resetResend: "Reset and resend",
      resetResendConfirm: "Reset already sent and resend this report over WhatsApp?",
      resetResendSuccess: "Report resent successfully.",
      resettingResend: "Resending...",
      resetResendAll: "Reset and resend all",
      resetResendAllConfirm: "Reset already sent and resend all ready reports over WhatsApp?",
      resetResendAllSuccess: "Ready reports resent successfully.",
      resettingResendAll: "Resending all...",
    },
    whatsappPage: {
      title: "WhatsApp recipients and deliveries",
      subtitle:
        "Configure recipient delivery gates and review deliveries recorded this week. An active mapping alone never receives automatic podcasts.",
      addRecipient: "Add recipient",
      weeklyTitle: "Deliveries this week",
      weeklySubtitle: "Review delivery attempts recorded by day.",
      viewFullLog: "View full log",
      noDeliveries: "No deliveries were recorded for this day.",
      deliveriesLoadingError: "Could not load this week's deliveries.",
      deliverySingular: "delivery",
      deliveryPlural: "deliveries",
      deliveryListLabel: "Deliveries for the selected day",
      recentDeliveriesNotice: "Showing the 5 most recent deliveries for this day.",
      contentReport: "Report",
      contentDailyPodcast: "Daily summary",
      noContentReference: "No reference",
      sourceAutomation: "Automation",
      sourceReportReady: "Report ready",
      sourceDailyReady: "Daily summary ready",
      sourceManual: "Manual",
      sourceManualResend: "Manual resend",
      sourceBulkResend: "Bulk resend",
      sourceUnknown: "Unknown",
      statusProcessing: "Processing",
      statusSent: "Sent",
      statusDelivered: "Delivered",
      statusRead: "Read",
      statusError: "Error",
      recipientsTitle: "Configured recipients",
      recipientsSubtitle:
        "Link each number to a user. The role only tailors messaging; automatic eligibility also requires audience membership and matching switches.",
      emptyRecipients: "No recipients have been configured yet.",
      noSearchResults: "No recipients match this search.",
      recipientSingular: "recipient",
      recipientPlural: "recipients",
      tableUser: "RECIPIENT",
      tablePhone: "WHATSAPP NUMBER",
      tableRole: "ASSIGNED ROLE",
      tableActivation: "LAST ACTIVATION",
      tableStatus: "STATUS",
      tableActions: "ACTIONS",
      inheritRole: "Use the user's role",
      roleCeo: "CEO",
      roleProductionManager: "Production Manager",
      roleCenterManager: "Center Manager",
      roleVeterinarian: "Veterinarian",
      roleQualityManager: "Quality Manager",
      roleOperations: "Operations",
      roleFinance: "Finance",
      createTitle: "Add recipient",
      createSubtitle: "Link a WhatsApp number to a user and configure its availability for future deliveries.",
      editTitle: "Edit recipient",
      editSubtitle: "Update the assigned role and recipient status.",
      fieldUser: "User",
      fieldPhone: "WhatsApp number",
      fieldRole: "Messaging role (optional)",
      activeTitle: "Active recipient",
      activeSubtitle: "This is the master gate; report and daily podcasts remain independent opt-ins.",
      activeStatus: "Active",
      inactiveStatus: "Inactive",
      enableRecipient: "Activate recipient",
      disableRecipient: "Pause recipient",
      editAction: "Edit",
      deleteAction: "Delete",
      createAction: "Add recipient",
      saveChanges: "Save changes",
      deleteConfirm:
        "Delete this recipient? They will stop receiving content, but their delivery history will be preserved.",
      monday: "Monday",
      tuesday: "Tuesday",
      wednesday: "Wednesday",
      thursday: "Thursday",
      friday: "Friday",
      saturday: "Saturday",
      sunday: "Sunday",
    },
  },
};

export function getMessages(locale: AdminLocale) {
  return messages[locale];
}

export function formatLocaleDateTime(value: string, locale: AdminLocale) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString(locale === "en" ? "en-US" : "es-CL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function useI18n() {
  const hydrate = useAdminStore((state) => state.hydrate);
  const locale = useAdminStore((state) => state.locale);
  const setLocale = useAdminStore((state) => state.setLocale);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const t = useMemo(() => getMessages(locale), [locale]);

  return {
    locale,
    setLocale,
    t,
    formatDateTime: (value: string) => formatLocaleDateTime(value, locale),
  };
}
